import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
// HttpParams not needed - using inline params objects
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { getGaAttribution } from '../utils/ga-attribution.util';

@Injectable({ providedIn: 'root' })
export class OrderService {
  private apiUrl = environment.apiUrl;

  /**
   * NEW backend — where an order is placed and paid for.
   *
   * The old `PUT /api/auth/generateOrder` did two things wrong. It re-fetched the
   * cart through the old repricing endpoint, so the order never billed what the
   * page had shown (₹18,252 against ₹21,233.90); and for any cart line the new
   * backend wrote it billed ZERO, because it multiplies
   * `pricing_detail.amount_payable` by quantity and the new shape stores
   * `grossIncl`/`total` instead. Six live cart rows were measured producing ₹0
   * orders against ₹20,880 owed.
   */
  private siteApi = `${environment.newApiUrl}/api/site`;

  constructor(private http: HttpClient, private auth: AuthService) {}

  private get sessionId(): string {
    let sid = localStorage.getItem('session_id');
    if (!sid) {
      sid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
      localStorage.setItem('session_id', sid);
    }
    return sid;
  }

  private authHeaders(): HttpHeaders {
    const token = this.auth.token;
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    });
  }

  private get isLoggedIn(): boolean {
    return this.auth.isLoggedIn;
  }

  // ── Cart ───────────────────────────────────────────────────────
  addToCart(customizedProduct: any): Observable<any> {
    const attr = getGaAttribution();
    if (this.isLoggedIn) {
      return this.http.post(`${this.apiUrl}/api/auth/cart`, { customizedProduct, ...attr }, { headers: this.authHeaders() });
    }
    return this.http.post(`${this.apiUrl}/api/unauth/cart_guest`, { session_id: this.sessionId, customizedProduct, ...attr });
  }

  getCart(): Observable<any> {
    if (this.isLoggedIn) {
      // Use customers_id (not user id) — this matches cart.customer_id in DB
      const customerId = Number(this.auth.currentUser?.customers_id) || Number(this.auth.currentUser?.id) || 0;
      const filter = JSON.stringify([['customer_id', '=', customerId]]);
      return this.http.get(`${this.apiUrl}/api/auth/cart`, {
        headers: this.authHeaders(),
        params: { filter, purchaseType: 'customer', customerDiscount: '0', orderBy: JSON.stringify([['created_at', 'desc']]), pageNumber: '0', pageSize: '-1' }
      });
    }
    const filter = JSON.stringify([['session_id', '=', this.sessionId]]);
    return this.http.get(`${this.apiUrl}/api/unauth/cart`, {
      params: { filter, purchaseType: 'Inventory', orderBy: '[]', pageNumber: '0', pageSize: '-1' }
    });
  }

  updateQuantity(customizedProductId: number, quantity: number, promoId = 0): Observable<any> {
    const payload = { customized_product_id: customizedProductId, quantity, promo_id: promoId, session_id: this.sessionId };
    const url = this.isLoggedIn ? `${this.apiUrl}/api/auth` : `${this.apiUrl}/api/unauth`;
    return this.http.put(`${url}/updateCustomizedProductQuantity`, payload,
      this.isLoggedIn ? { headers: this.authHeaders() } : {});
  }

  updateGiftWrap(cartItemId: number, giftWrap: boolean): Observable<any> {
    return this.http.put(`${this.apiUrl}/api/auth/updateGiftWrap`,
      { id: cartItemId, gift_wrap: giftWrap ? 1 : 0 }, { headers: this.authHeaders() });
  }

  updateInstallation(cartItemId: number, installation: boolean, pincode: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/api/auth/updateInstallation`,
      { id: cartItemId, installation: installation ? 1 : 0, installation_pincode: pincode },
      { headers: this.authHeaders() });
  }

  removeCartItem(cartItemId: number): Observable<any> {
    const ids = JSON.stringify([cartItemId]);
    if (this.isLoggedIn) {
      return this.http.delete(`${this.apiUrl}/api/auth/cart`, { headers: this.authHeaders(), params: { ids } });
    }
    return this.http.delete(`${this.apiUrl}/api/unauth/cart-guest`, { params: { ids, session_id: this.sessionId } });
  }

  // ── Address ────────────────────────────────────────────────────
  getAddresses(): Observable<any> {
    // Logged-in addresses read from the NEW backend — the owner is resolved from
    // the site JWT there, so no user_id filter is sent. Guest still reads the old
    // backend (unauth, no token involved).
    if (this.isLoggedIn) {
      return this.http.get(`${this.siteApi}/addresses`, { headers: this.authHeaders() });
    }
    const filter = JSON.stringify([['session_id', '=', this.sessionId]]);
    return this.http.get(`${this.apiUrl}/api/unauth/customer_address`, {
      params: { filter, pageNumber: '0', pageSize: '-1', orderBy: '[]' }
    });
  }

  addAddress(address: any): Observable<any> {
    if (this.isLoggedIn) {
      return this.http.post(`${this.siteApi}/addresses`, address, { headers: this.authHeaders() });
    }
    return this.http.post(`${this.apiUrl}/api/unauth/customer_address_guest`, {
      ...address, session_id: this.sessionId
    });
  }

  updateAddress(address: any): Observable<any> {
    return this.http.put(`${this.siteApi}/addresses/${address.id}`, address, { headers: this.authHeaders() });
  }

  deleteAddress(id: number): Observable<any> {
    return this.http.delete(`${this.siteApi}/addresses/${id}`, { headers: this.authHeaders() });
  }

  getAddressById(id: number): Observable<any> {
    return this.http.get(`${this.siteApi}/addresses/${id}`, { headers: this.authHeaders() });
  }

  // ── Cities ─────────────────────────────────────────────────────
  searchCities(query: string): Observable<any> {
    const filter = JSON.stringify([['search_key', query]]);
    return this.http.get(`${this.apiUrl}/api/unauth/city`, { params: { filter, pageNumber: '0' } });
  }

  // ── Shipping ───────────────────────────────────────────────────
  /*
   * ── REMOVED: getShippingMethods() and getShippingAmount() ────────────────
   *
   * Both replaced by getSiteShippingOptions(), which returns the options already
   * priced. They are deleted rather than deprecated because between them they
   * were the whole reason the page showed one delivery figure and charged
   * another:
   *
   *   getShippingMethods   returned the raw `shipping_method` rate card, from
   *                        which the option card rendered `amount` (₹150) with
   *                        no tax and no free-delivery rule
   *
   *   getShippingAmount    hit /api/unauth/getShippingAmt — which exists in
   *                        NEITHER backend and has always answered 404. The
   *                        pincode-specific pricing it was meant to fetch, and
   *                        the "Express Delivery ₹299" fallback beside it, never
   *                        ran once
   *
   * With the list unpriced, the page filled the gap itself: a ₹499 threshold, a
   * "first method is standard" assumption, and a tax rate read off the cart's own
   * GST slab. Three of the four decisions that belong to the server.
   */


  // ── Promo / Coupon ─────────────────────────────────────────────
  getPromos(): Observable<any> {
    const userId = this.auth.currentUser?.id || this.sessionId;
    const filter = JSON.stringify([
      ['unexpired', String(userId)],
      ['status', '=', '1'],
      ['discount_amount', '>', '0']
    ]);
    return this.http.get(`${this.apiUrl}/api/unauth/promo`, {
      params: { filter, pageNumber: '0', pageSize: '-1', orderBy: '[]' }
    });
  }

  // ── Order ──────────────────────────────────────────────────────

  /**
   * Place the order — on the NEW backend. MIGRATED from generateOrder.
   *
   * The payload is much smaller than the old one, and everything missing from it
   * is missing on purpose: no `customer_id` (the token says who), no amount or
   * total (the server prices the cart), no `customer_discount`, no
   * `payment_method`, no `is_guest`. The old endpoint accepted all of those from
   * the browser.
   */
  placeSiteOrder(payload: {
    address_id: number;
    shipping_address_id?: number | null;
    cart_list?: number[] | null;
    shipping_method_id?: number | null;
    // The coupon CODE, never an amount. The server decides whether it applies and
    // for how much, and returns what it actually took off — the reverse of the
    // PayU arrangement, where this page computed the discount and the gateway was
    // handed the result to charge.
    promo_code?: string | null;
    gift_wrap?: boolean;
    gift_message?: string | null;
    notes?: string | null;
  }): Observable<any> {
    return this.http.post(`${this.siteApi}/orders`, payload, { headers: this.authHeaders() });
  }

  /**
   * The delivery options, PRICED by the server.
   *
   * Replaces the pair of old-backend calls this page used to make —
   * `/api/unauth/shipping_method` for the list and `/api/unauth/getShippingAmt`
   * for the amounts. The second answered 404 in both backends, always has, so the
   * page fell into its error branch and priced delivery from the raw rate card
   * while computing the tax itself.
   *
   * Each option carries `total`: what the customer pays and what will be charged.
   * The free-delivery rule, the tax split and the pincode-based rate set are all
   * resolved server-side, so there is nothing left here to disagree with.
   */
  getSiteShippingOptions(cartList?: number[] | null): Observable<any> {
    const params: any = {};
    if (cartList && cartList.length) {
      params['cart_list[]'] = cartList.map(String);
    }

    return this.http.get(`${this.siteApi}/shipping/options`, { headers: this.authHeaders(), params });
  }

  /**
   * The checkout's products + installation (with its GST) for the rows being
   * bought, priced by the order's own code — the page shows these instead of
   * summing the cart rows. Buy Now passes its selection.
   */
  getSiteCheckoutQuote(cartList?: number[] | null): Observable<any> {
    const params: any = {};
    if (cartList && cartList.length) {
      params['cart_list[]'] = cartList.map(String);
    }

    return this.http.get(`${this.siteApi}/checkout/quote`, { headers: this.authHeaders(), params });
  }

  /**
   * Open a Razorpay order for one of ours.
   *
   * Sends only OUR order id. The amount comes back from the server, having been
   * read from the `order` row — the browser never proposes it. This is the whole
   * difference from PayU, where the amount AND its signing salt both lived in
   * this bundle.
   */
  razorpayStart(orderId: number): Observable<any> {
    return this.http.post(`${this.siteApi}/payments/razorpay/start`, { order_id: orderId }, { headers: this.authHeaders() });
  }

  /**
   * Tell the server the payment window was closed without paying.
   *
   * Without this the outcome was recorded nowhere: `start()` writes a `payment`
   * row as 'initiated' and only a capture or a failure webhook ever moved it, so
   * an abandoned attempt stayed 'initiated' for ever — identical in the database
   * to a customer who is paying right this second.
   *
   * Fire-and-forget from the caller's point of view. It reports something already
   * observed, so a failed report must not put an error in front of the customer;
   * the order and cart are untouched either way and a retry just works.
   */
  razorpayCancel(payload: { order_id: number; razorpay_order_id?: string | null; reason?: string | null }): Observable<any> {
    return this.http.post(`${this.siteApi}/payments/razorpay/cancel`, payload, { headers: this.authHeaders() });
  }

  /**
   * Report the modal's result back for verification.
   *
   * Nothing here is trusted on its face: the server checks the signature and then
   * asks Razorpay directly whether that payment was captured and for how much.
   */
  razorpayVerify(payload: {
    order_id: number;
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }): Observable<any> {
    return this.http.post(`${this.siteApi}/payments/razorpay/verify`, payload, { headers: this.authHeaders() });
  }

  /**
   * One of the shopper's own orders, from the new backend.
   *
   * Scoped to the caller's `user_id` server-side, unlike the old
   * `/api/unauth/orderById/{id}` — which is unauthenticated, so any guessed id
   * returns that customer's name, address and phone number to anyone who asks.
   */
  getSiteOrder(ref: string): Observable<any> {
    return this.http.get(`${this.siteApi}/orders/${encodeURIComponent(ref)}`, { headers: this.authHeaders() });
  }

  beginCheckout(payload: { customer_id: number; value: number; coupon?: string }): Observable<any> {
    const attr = getGaAttribution();
    return this.http.post(`${this.apiUrl}/api/auth/beginCheckout`, { ...payload, ...attr }, { headers: this.authHeaders() });
  }

  getOrder(orderId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/api/unauth/orderById/${orderId}`);
  }

  /*
   * ── REMOVED: paymentHook() and failedPaymentHook() ───────────────────────
   *
   * These posted to the old backend's /api/unauth/paymentHook and
   * /api/unauth/failedPaymentHook — PayU's redirect-return hooks. paymentHook
   * marks an order paid straight from the request body, with NO signature, hash
   * or gateway verification (proven: an unauthenticated POST with
   * status=success flipped a ₹25,206 order to paid). Only the now-deleted
   * PaymentSuccess page ever called it, and failedPaymentHook had no caller at
   * all.
   *
   * Razorpay does not use them. Its outcome is confirmed by
   * razorpayVerify()/the webhook, which check the signature and re-fetch the
   * payment from Razorpay before an order is marked paid — see
   * SitePaymentController on the new backend. Deleted rather than left dead so
   * nothing can call the unverified endpoints again from here.
   *
   * NOTE: the endpoints themselves are still live on the OLD backend and remain
   * exploitable until removed there — that is a separate, server-side change.
   */
}
